import React, { useState, useEffect } from 'react';
import { Search, QrCode, Receipt } from 'lucide-react';
import { Input } from '../ui/input';
import { adminQrRedemptionsAPI } from '../../utils/api';
import { toast } from '../../hooks/use-toast';
import { Toaster } from '../ui/toaster';

const QrRedemptionsManagement = () => {
  const [redemptions, setRedemptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    email: '',
    ticket_number: '',
    date_from: '',
    date_to: '',
  });
  const [pagination, setPagination] = useState({
    current_page: 1,
    last_page: 1,
    per_page: 15,
    total: 0,
  });

  useEffect(() => {
    loadRedemptions();
  }, [filters]);

  const loadRedemptions = async (page = 1) => {
    try {
      setLoading(true);
      const params = {
        page,
        per_page: 15,
        ...(filters.email && { email: filters.email }),
        ...(filters.ticket_number && { ticket_number: filters.ticket_number }),
        ...(filters.date_from && { date_from: filters.date_from }),
        ...(filters.date_to && { date_to: filters.date_to }),
      };
      const response = await adminQrRedemptionsAPI.getAll(params);
      setRedemptions(response.data);
      if (response.pagination) {
        setPagination(response.pagination);
      }
    } catch (error) {
      toast({
        title: "Error",
        description: error.message || "No se pudieron cargar los canjes",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-6 space-y-6">
      <Toaster />
      <div>
        <h2 className="text-2xl font-bold text-white">Canjes de QR</h2>
        <p className="text-[#A2A2A2]">Vista de canjes de QR y tickets de compra (solo lectura)</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div>
          <Input
            type="text"
            placeholder="Buscar por email..."
            value={filters.email}
            onChange={(e) => setFilters({ ...filters, email: e.target.value })}
            className="bg-white/5 border-white/10 text-white"
          />
        </div>
        <div>
          <Input
            type="text"
            placeholder="Buscar por ticket..."
            value={filters.ticket_number}
            onChange={(e) => setFilters({ ...filters, ticket_number: e.target.value })}
            className="bg-white/5 border-white/10 text-white"
          />
        </div>
        <div>
          <Input
            type="date"
            placeholder="Fecha desde"
            value={filters.date_from}
            onChange={(e) => setFilters({ ...filters, date_from: e.target.value })}
            className="bg-white/5 border-white/10 text-white"
          />
        </div>
        <div>
          <Input
            type="date"
            placeholder="Fecha hasta"
            value={filters.date_to}
            onChange={(e) => setFilters({ ...filters, date_to: e.target.value })}
            className="bg-white/5 border-white/10 text-white"
          />
        </div>
      </div>

      {loading ? (
        <div className="text-center py-12 text-[#A2A2A2]">Cargando...</div>
      ) : redemptions.length === 0 ? (
        <div className="text-center py-12 text-[#A2A2A2]">No hay canjes registrados</div>
      ) : (
        <div className="bg-white/5 border border-white/10 rounded-2xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[900px]">
            <thead className="bg-white/5">
              <tr>
                <th className="px-6 py-4 text-left text-sm font-medium text-white">Suscriptor</th>
                <th className="px-6 py-4 text-left text-sm font-medium text-white">QR / Promoción</th>
                <th className="px-6 py-4 text-left text-sm font-medium text-white">Ticket</th>
                <th className="px-6 py-4 text-left text-sm font-medium text-white">Total</th>
                <th className="px-6 py-4 text-left text-sm font-medium text-white">Fecha canje</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/10">
              {redemptions.map((redemption) => (
                <tr key={redemption.id} className="hover:bg-white/5">
                  <td className="px-6 py-4">
                    <div>
                      <div className="text-white font-medium">{redemption.user.name}</div>
                      <div className="text-[#A2A2A2] text-sm">{redemption.user.email}</div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-2">
                      <QrCode className="w-4 h-4 text-[#A2A2A2]" />
                      <div>
                        <div className="text-white text-sm">{redemption.promotion_name || redemption.qr_code}</div>
                        {redemption.store && (
                          <div className="text-[#A2A2A2] text-xs">{redemption.store.name}</div>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    {redemption.ticket.number ? (
                      <div className="flex items-center gap-2">
                        <Receipt className="w-4 h-4 text-[#A2A2A2]" />
                        <div>
                          <div className="text-white text-sm">#{redemption.ticket.number}</div>
                          {redemption.ticket.date && (
                            <div className="text-[#A2A2A2] text-xs">{new Date(redemption.ticket.date).toLocaleDateString()}</div>
                          )}
                        </div>
                      </div>
                    ) : (
                      <span className="text-[#A2A2A2]">-</span>
                    )}
                  </td>
                  <td className="px-6 py-4 text-white">
                    {redemption.ticket.total ? `$${redemption.ticket.total.toFixed(2)}` : '-'}
                  </td>
                  <td className="px-6 py-4 text-[#A2A2A2]">
                    {new Date(redemption.redeemed_at).toLocaleString()}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        </div>
      )}

      {pagination.last_page > 1 && (
        <div className="flex items-center justify-between">
          <div className="text-[#A2A2A2] text-sm">
            Mostrando {((pagination.current_page - 1) * pagination.per_page) + 1} a {Math.min(pagination.current_page * pagination.per_page, pagination.total)} de {pagination.total}
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => loadRedemptions(pagination.current_page - 1)}
              disabled={pagination.current_page === 1}
              className="px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white disabled:opacity-50 disabled:cursor-not-allowed hover:bg-white/10"
            >
              Anterior
            </button>
            <button
              onClick={() => loadRedemptions(pagination.current_page + 1)}
              disabled={pagination.current_page === pagination.last_page}
              className="px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white disabled:opacity-50 disabled:cursor-not-allowed hover:bg-white/10"
            >
              Siguiente
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default QrRedemptionsManagement;

